import React, { FC, useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from '../../../store';
import { RootState } from '../../../rootReducer';
import { setLocalizationDto, getFindingLocalizationDto, setStatusTable } from '../Annotation/annotation.slice';
import { AnnotationsForGrid, MLRequest, PathContextType } from '../Annotation/annotation.types';
import { Button, Divider } from 'antd';
import {
  setSelectedAnnotationForEdit,
  setShowCreateAnnotationModal,
  setAnnotationIdsForDelete,
  setShowDeleteAnnotationDialog,
  setQAReviewPassedFromGrid
} from '../Annotation/AnnotationForm/AnnotationForm.slice';
import { ValidationModalDto } from '../Annotation/AnnotationValidationModal/AnnotationValidationModal.slice';
import { getMLRequestAndValidationResultForNotUpdatedShape } from '../Annotation/AnnotationForm/Helpers';
import { UseHaveAnyPermissions } from '../../../Auth/UseHaveAnyPermissions';
import { Permissions } from '../../../shared/models/permissions.enum';
import { TABLE_STATUS } from '../../../shared/Helpers';
import { AnnotationManagerPrefix } from '../Annotation/Helpers';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ParserContext } from '../contexts/ParserContext';
import { SetQAReviewPassedDto } from '../Annotation/AnnotationForm/AnnotationForm.types';

type Props = {
  canUseAnnotationTool: boolean;
  tableStatus: number;
  pathContext: PathContextType;
  onAnnotationEdit: (record: AnnotationsForGrid) => void;
  onShowModal: () => void;
  onResetLocalization: () => void;
  onShowDeleteDialog: () => void;
  onSetAnnotationIdsForDelete: (ids: string[]) => void;
  onLoadLocalization: (mlRequest: MLRequest, validationDto: ValidationModalDto, isUpdate?: boolean) => void;
  onSetStatusTable: (expandHide: number) => void;
  onCallSetQAReviewPassedFromGrid: (setQAReviewPassedDto: SetQAReviewPassedDto) => void;
};

type AnnotationsTableActionProps = {
  record: AnnotationsForGrid;
};

const AnnotationsTableAction: FC<RouteComponentProps<any> & Props & AnnotationsTableActionProps> = ({
  canUseAnnotationTool,
  record,
  tableStatus,
  history,
  pathContext,
  onAnnotationEdit,
  onShowDeleteDialog,
  onSetAnnotationIdsForDelete,
  onLoadLocalization,
  onSetStatusTable,
  onCallSetQAReviewPassedFromGrid
}) => {
  const canEdit = UseHaveAnyPermissions([
    Permissions.AnnotationManager_ViewComments,
    Permissions.AnnotationManager_CreateAnnotation,
    Permissions.AnnotationManager_RequestAssessment,
    Permissions.AnnotationManager_SetResponseOnAssessment,
    Permissions.AnnotationManager_AnnotationStandardAdministration
  ]);

  const canSetQAreviewStatus = UseHaveAnyPermissions([Permissions.AnnotationManager_QAReview]);

  const { parser } = useContext(ParserContext);

  const navigateToFinding = useCallback(
    (newSequencePath: string, imageName: string, id: string) => {
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${newSequencePath}/${imageName}/${id}`);

      if (tableStatus === TABLE_STATUS.EXPANDED) {
        onSetStatusTable(TABLE_STATUS.NORMAL);
      }
    },
    [history, onSetStatusTable, parser.name, tableStatus]
  );

  const editAnnotation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    if (pathContext === 'no-images') {
      navigateToFinding(record.sequencePath, record.imageName, record.id);
    }

    const { mlRequest, validationResult } = getMLRequestAndValidationResultForNotUpdatedShape(record);

    onLoadLocalization(mlRequest, validationResult);

    onAnnotationEdit(record);
  };

  const deleteAnnotation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    onSetAnnotationIdsForDelete([record.id]);
    onShowDeleteDialog();
  };

  const setQAReviewPassed = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    const setQAReviewPassedDto: SetQAReviewPassedDto = {
      sequencePathId: record.sequencePath,
      imageFileName: record.imageName,
      id: record.id
    };

    onCallSetQAReviewPassedFromGrid(setQAReviewPassedDto);
  };

  return (
    <div>
      <Button disabled={!canEdit} type='link' onClick={editAnnotation}>
        Edit
      </Button>
      <Divider type='vertical' />
      <Button disabled={!record.canBeDeleted} type='link' onClick={deleteAnnotation}>
        Delete
      </Button>
      <Divider type='vertical' />
      {record.showQAReviewPassedOption && canSetQAreviewStatus && (
        <Button type='link' onClick={setQAReviewPassed}>
          Passed
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canUseAnnotationTool: state.annotation.havePermissionToAnnotate,
  tableStatus: state.annotation.tableStatus,
  pathContext: state.annotation.pathContext
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onAnnotationEdit: (record: AnnotationsForGrid) => dispatch(setSelectedAnnotationForEdit(record)),
  onShowModal: () => dispatch(setShowCreateAnnotationModal(true)),
  onResetLocalization: () => dispatch(setLocalizationDto(null)),
  onShowDeleteDialog: () => dispatch(setShowDeleteAnnotationDialog(true)),
  onSetAnnotationIdsForDelete: (ids: string[]) => dispatch(setAnnotationIdsForDelete(ids)),
  onLoadLocalization: (mlRequest: MLRequest, validationDto: ValidationModalDto, isUpdate: boolean = false) =>
    dispatch(getFindingLocalizationDto(mlRequest, validationDto, isUpdate)),
  onSetStatusTable: (expandHide: number) => dispatch(setStatusTable(expandHide)),
  onCallSetQAReviewPassedFromGrid: (setQAReviewPassedDto: SetQAReviewPassedDto) => dispatch(setQAReviewPassedFromGrid(setQAReviewPassedDto))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AnnotationsTableAction));
